import { useEffect, useState } from 'react'
import type { DayDoc, DayResult, HistogramSummary } from '../lib/types'
import { binOf, formatBig, emojiFor, timesOff } from '../lib/scoring'
import { loadSummary, percentileOf, chimpWins } from '../lib/histogram'
import { dailyStreak, sharpStreak, saveResult } from '../lib/storage'
import { isPlus, isSignedIn } from '../lib/user'
import { share } from '../lib/share'
import { Curve } from './Curve'
import { InfoTip } from './InfoTip'
import { TopBar } from './TopBar'
import { BottomNav } from './BottomNav'
import { BonusCard } from './BonusCard'
import { AuthModal } from './AuthModal'

export function Results({ day, result }: { day: DayDoc; result: DayResult }) {
  const [sum, setSum] = useState<HistogramSummary | null>(null)
  const [pct, setPct] = useState<number | undefined>(result.percentile)
  const [chimp, setChimp] = useState<number | undefined>(result.chimpWins)
  const [toast, setToast] = useState('')
  const [nudge, setNudge] = useState(false)

  // the right moment: ~1s after the result lands, once per session, max once a week — anon only
  useEffect(() => {
    if (isSignedIn()) return
    if (sessionStorage.getItem('wayoff.nudged')) return
    const last = Number(localStorage.getItem('wayoff.nudgeAt') ?? 0)
    if (Date.now() - last < 7 * 86400000) return
    const t = setTimeout(() => setNudge(true), 1200)
    return () => clearTimeout(t)
  }, [])
  const closeNudge = () => {
    setNudge(false)
    sessionStorage.setItem('wayoff.nudged', '1')
    localStorage.setItem('wayoff.nudgeAt', String(Date.now()))
  }

  useEffect(() => {
    let dead = false
    // our own shard write may still be in flight — retry until the crowd appears (max 3 tries)
    const attempt = (n: number) => loadSummary(day).then((s) => {
      if (dead) return
      setSum(s)
      const p = percentileOf(result.score, s.scores), c = chimpWins(result.results, s.q)
      setPct(p); setChimp(c)
      if (p !== result.percentile || c !== result.chimpWins) saveResult({ ...result, percentile: p, chimpWins: c })
      if (s.players === 0 && n < 3) setTimeout(() => attempt(n + 1), 1500 * (n + 1))
    })
    attempt(0)
    return () => { dead = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day.date])

  const [sel, setSel] = useState(0) // default: question 1 (index 0); the hero is at the end
  const hero = day.questions[sel]
  const heroR = result.results[sel]
  const heroBins = sum?.q[hero.id] ?? {}
  const heroTotal = Object.values(heroBins).reduce((a, b) => a + b, 0)
  const streak = dailyStreak(undefined, isPlus()), sharp = sharpStreak()

  const doShare = async () => {
    const text = `Way Off ${day.number > 0 ? `#${day.number} · ` : ''}${day.theme.emoji} ${day.theme.name}\n${result.emoji}\n${result.score.toLocaleString('en-US')}/5,000${pct != null ? ` · closer than ${pct}% today` : ''}${chimp != null ? `\n🐒 beat the chimp ${chimp}/5` : ''}\nYou'll be way off → https://wayoffgame.com`
    const r = await share(text)
    setToast(r === 'copied' ? 'Copied to clipboard' : r === 'shared' ? 'Shared!' : 'Could not share')
    setTimeout(() => setToast(''), 1800)
  }

  return (
    <div className="screen results wide">
      <TopBar />
      <div className="res-head">
        <h1>{result.score >= 4000 ? 'Frighteningly sharp.' : result.score >= 2500 ? 'Not bad, human.' : 'Way off.'}</h1>
        {streak > 0 && <span className="streak-chip" title={`${sharp} sharp in a row`}>🔥 {streak}</span>}
      </div>
      <p className="sub">{day.theme.emoji} {day.theme.name}{day.number > 0 ? ` · #${day.number}` : ''}{result.replay ? ' · replay' : ''}</p>
      <div className="res-grid">
      <div className="res-left">
      <div className="row">
        <div className="card"><h3>Score</h3><div className="score">{result.score.toLocaleString('en-US')}<small>/5,000</small></div></div>
        <div className="card"><h3>Today</h3><div className="stat">{pct != null ? <>closer than <b>{pct}%</b></> : <span className="muted">first players in…</span>}</div>
          {chimp != null && <div className="tiny" style={{ marginTop: 4 }}>🐒 {chimp >= 3 ? `you beat the chimp ${chimp}–${5 - chimp}` : `the chimp got you ${5 - chimp}–${chimp}`}<InfoTip>On every question, the chimp "guesses" the crowd's middle answer. Beat its score on a question and it's your point.</InfoTip></div>}
        </div>
      </div>

      </div>
      <div className="res-right">
      <div className="card" id="curve">
        <h3>How you did versus everyone else</h3>
        <div className="qpick">
          {day.questions.map((q, k) => <button key={q.id} className={k === sel ? 'on' : ''} onClick={() => setSel(k)} aria-label={`question ${k + 1}`}>{emojiFor(result.results[k].points)}</button>)}
        </div>
        <p className="muted" style={{ margin: '8px 0 4px', fontSize: 14 }}>{sel + 1}. {hero.prompt}</p>
        {heroTotal > 0 ? <Curve bins={heroBins} truthBin={binOf(hero.answer)} youBin={binOf(heroR.guess)} /> : <p className="muted">The crowd curve appears once a few people have played.</p>}
        <div className="curve-verdict">
          <span>you: <b>{formatBig(heroR.guess)}</b></span>
          <span>truth: <b>{formatBig(hero.answer)} {hero.unit}</b></span>
          <span className="apart">{heroR.guess === hero.answer ? 'spot on 🎯' : `${timesOff(heroR.ratio)} apart`} · +{heroR.qscore.toLocaleString('en-US')}</span>
        </div>
      </div>

      </div>
      <div className="res-bottom">
      {!result.replay && <BonusCard hideIfPlayed />}
      {!result.replay && day.extended && day.extended.length > 0 && (
        isPlus()
          ? <a className="card upsell" href="#/ext"><span>✦</span><span><b>5 more on {day.theme.name}</b> — your extended pack is ready.</span><span>→</span></a>
          : <a className="card upsell" href="#/plus"><span>✦</span><span><b>Want 5 more on the subject of {day.theme.name}?</b><br />Extended packs are a Plus thing.</span><span>→</span></a>
      )}
      <div className="actions">
        <button className="btn block" onClick={doShare}>Share (no spoilers, just bragging rights) ↗</button>
        {!result.replay && <a className="btn ghost block" href="#/stats">See your stats</a>}
        {result.replay && <a className="btn ghost block" href="#/">Back to today</a>}
      </div>

      </div>
      </div>
      <p className="tiny center" style={{ marginTop: 18 }}>A new batch to blow your mind drops at midnight UTC, daily.</p>
      <BottomNav />
      {toast && <div className="toast">{toast}</div>}
      {nudge && <AuthModal nudge onClose={closeNudge} />}
    </div>
  )
}
